import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../../services/general/error';
import GeneralService from '../../../../../../../services/general';
import VesselRequisitionService from '../../../../../../../services/vessels/requisitions/requisition';
import VesselRequisitionItemService from '../../../../../../../services/vessels/requisitions/item';

// props
import { IVesselRequisitionResourceShortProps } from '../../../../../../../props/vessels/requisitions/requisition';
import { IVesselRequisitionItemResourceShortProps } from '../../../../../../../props/vessels/requisitions/item';

// components
import { DefaultButton, DetailsList, IColumn, Panel, PanelType, PrimaryButton, SelectionMode, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import Text from '../../../../../../typography/text';
import Label from '../../../../../../typography/label';
import Details from '../../../../../../typography/details';
import VesselRequisitionItemPricingService from '../../../../../../../services/vessels/requisitions/itemPricing';

interface ApproveVesselRequisitionItemPricingProps {
    requisition?: IVesselRequisitionResourceShortProps;
    items?: IVesselRequisitionItemResourceShortProps[];
    requisitionId?: string;
    itemIds?: string[];
    onDismissed(refresh?: boolean, status?: string): void;
}

const ApproveVesselRequisitionItemPricing: React.FC<ApproveVesselRequisitionItemPricingProps> = (props: ApproveVesselRequisitionItemPricingProps) => {
    const { banner, user } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [requisition, setRequisition] = React.useState<IVesselRequisitionResourceShortProps | undefined>();
    const [selected, setSelected] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);

    const columns: IColumn[] = [
        {
            key: 'details',
            name: 'Details',
            minWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack tokens={{ childrenGap: 10 }}>
                    <Stack tokens={{ childrenGap: 3 }}>
                        <Label size={'small'}>{item.goods.name}</Label>
                        {item.notes ? <Text style={{ fontStyle: 'italic' }} size={'small'}>Notes: {item.notes}</Text> : null}
                    </Stack>
                    <Stack tokens={{ childrenGap: 3 }}>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Text size={'small'}>Purpose</Text>
                            <Text size={'small'}>:</Text>
                            <Text size={'small'}>{item.purpose || "-"}</Text>
                        </Stack>
                    </Stack>
                    <Stack tokens={{ childrenGap: 3 }}>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Text size={'small'}>Size</Text>
                            <Text size={'small'}>:</Text>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.length))} cm x {GeneralService.getNumberWithSeparator(Number(item.goods?.width))} cm x {GeneralService.getNumberWithSeparator(Number(item.goods?.height))} cm</Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Text size={'small'}>Weight</Text>
                            <Text size={'small'}>:</Text>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.weightAmount))} {item.goods?.weightUnit || ""}</Text>
                        </Stack>
                    </Stack>
                </Stack>
            }
        },
        {
            key: 'quantity',
            name: 'Quantity',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.quantityAmount))} {item.quantityUnit.name}</Text>
                </Stack>
            }
        },
        {
            key: 'vendor',
            name: 'Vendor',
            minWidth: 250,
            maxWidth: 250,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack>
                    <Text size={'small'}>{item.pricing?.vendor.name || "-"}</Text>
                </Stack>
            }
        },
        {
            key: 'totalPrice',
            name: 'Total Price',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(Number(item.pricing?.finalPrice))}</Text>
                </Stack>
            }
        }
    ]

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _requisition = undefined;
            if (props.requisitionId) {
                _requisition = await VesselRequisitionService.read(props.requisitionId);
            } else if (props.requisition) {
                _requisition = props.requisition;
            }

            let _selected: IVesselRequisitionItemResourceShortProps[] = [];
            if (_requisition && props.itemIds) {
                const _items = await VesselRequisitionItemService.retrieve(_requisition.id, `top=all`);
                _selected = _items.data.filter((item) => {
                    return (props.itemIds || []).indexOf(item.id) > -1;
                });
            } else if (props.items) {
                _selected = props.items;
            }

            setRequisition(_requisition);
            setSelected(_selected);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_requisition_items_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get requisition items. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);

            const fd = new FormData();
            selected.forEach((s) => {
                fd.append('ids[]', s.id);
            });

            await VesselRequisitionItemPricingService.approve(fd);
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={'Approve Vessel Requisition Items Pricing'}
        isOpen={true}
        type={PanelType.custom}
        customWidth={'920px'}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Approve"} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Approving requisition items pricing ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && requisition ? <>
                    <Text size={'small'}>Please confirm that you want to approve all requested items pricing below for <Label size={'xsmall'}>{requisition.vessel.name}</Label>:</Text>
                    <Stack className={'divider'}></Stack>
                    <Details title={'Name'} text={requisition.name} />
                    <Details title={'Vessel'} text={requisition.vessel.name} />
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={'Requested by'} text={requisition.createdBy.name} style={{width: '50%'}} />
                        <Details title={'Requested at'} text={moment(requisition.createdAt).format("DD/MM/YYYY HH:mm")} style={{width: '50%'}} />
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <DetailsList items={selected}
                        columns={columns}
                        selectionMode={SelectionMode.none} />
                </> : null
            }
        </Stack>
    </Panel>
};

export default ApproveVesselRequisitionItemPricing;
